import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { payrolls, type PayrollListItem } from '@/api/payrolls';
import { orgs } from '@/api/catalogs';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import DatePopover from '@/components/DatePopover';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MONTHS = ['', 'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
  'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];

export default function PayrollsPage() {
  const navigate = useNavigate();

  // Фільтри
  const [orgId, setOrgId] = useState('');
  const [kind, setKind] = useState<'' | 'MAIN' | 'ADVANCE'>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const orgName = useMemo(() => {
    const m = new Map<string, string>();
    orgList.data?.items.forEach((o) => m.set(o.id, o.name));
    return m;
  }, [orgList.data]);

  const list = useQuery({
    queryKey: ['payrolls', orgId, kind, dateFrom, dateTo],
    queryFn: () => payrolls.list({
      limit: 200,
      organization_id: orgId || undefined,
      payroll_kind: kind || undefined,
      // dateFrom/dateTo — YYYY-MM-DD; date_to включно до кінця дня.
      date_from: dateFrom ? `${dateFrom}T00:00:00` : undefined,
      date_to: dateTo ? `${dateTo}T23:59:59` : undefined,
    }),
  });

  const cols = useMemo<ColumnDef<PayrollListItem>[]>(() => [
    { key: 'document_number', header: '№', width: 130, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
    {
      key: 'organization', header: 'Організація', width: 180,
      sortValue: (r) => orgName.get(r.organization_id) ?? '',
      cell: (r) => orgName.get(r.organization_id) ?? '—',
    },
    {
      key: 'document_date', header: 'Дата', width: 160,
      sortValue: (r) => r.document_date ?? '',
      cell: (r) => r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '',
    },
    {
      key: 'period', header: 'Період', width: 140,
      sortValue: (r) => r.period_year * 100 + r.period_month,
      cell: (r) => `${MONTHS[r.period_month]} ${r.period_year}`,
    },
    {
      key: 'payroll_kind', header: 'Вид', width: 100, align: 'center',
      sortValue: (r) => r.payroll_kind ?? 'MAIN',
      cell: (r) => {
        const adv = r.payroll_kind === 'ADVANCE';
        return (
          <span style={{
            background: adv ? '#dbeafe' : '#dcfce7',
            color: adv ? '#1e40af' : '#166534',
            padding: '2px 8px', borderRadius: 4, fontSize: '0.85em',
          }}>
            {adv ? 'Аванс' : 'Основна'}
          </span>
        );
      },
    },
    {
      key: 'total_gross', header: 'Брутто', width: 130, align: 'right',
      sortValue: (r) => Number(r.total_gross),
      cell: (r) => fmt2(r.total_gross),
    },
    {
      key: 'total_net', header: 'До виплати', width: 130, align: 'right',
      sortValue: (r) => Number(r.total_net),
      cell: (r) => fmt2(r.total_net),
    },
    {
      key: 'posted', header: 'Проведено', width: 120, align: 'center',
      sortValue: (r) => r.posted ? 1 : 0,
      cell: (r) => (r.posted ? '✓ так' : 'ні'),
    },
  ], [orgName]);

  const hasFilter = orgId || kind || dateFrom || dateTo;

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Нарахування ЗП</h2>
        <button className="primary" onClick={() => navigate('/payrolls/new')}>
          + Нове нарахування
        </button>
      </div>

      {/* Фільтри: організація, вид, період (від-до). */}
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end',
                    flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <div className="field" style={{ minWidth: 200, margin: 0 }}>
          <label>Організація</label>
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            <option value="">— усі —</option>
            {orgList.data?.items.map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ minWidth: 140, margin: 0 }}>
          <label>Вид</label>
          <select value={kind} onChange={(e) => setKind(e.target.value as '' | 'MAIN' | 'ADVANCE')}>
            <option value="">— усі —</option>
            <option value="MAIN">Основна</option>
            <option value="ADVANCE">Аванс</option>
          </select>
        </div>
        <div className="field" style={{ minWidth: 150, margin: 0 }}>
          <label>Період з</label>
          <DatePopover value={dateFrom} onChange={setDateFrom} />
        </div>
        <div className="field" style={{ minWidth: 150, margin: 0 }}>
          <label>по</label>
          <DatePopover value={dateTo} onChange={setDateTo} />
        </div>
        {hasFilter && (
          <button onClick={() => { setOrgId(''); setKind(''); setDateFrom(''); setDateTo(''); }}>
            Скинути
          </button>
        )}
      </div>

      <DataTable<PayrollListItem>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/payrolls/${r.id}`)}
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
